import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Form, Spinner, Table } from 'react-bootstrap';
import {
  deleteOvrRecords,
  getDataSummary,
  getHiddenOvrRecords,
  hideAllOvrRecords,
  unhideOvrRecords,
} from '../services/api';
import { setAlert } from '../store/slices/alertSlice';
import ConfirmActionModal from './ConfirmActionModal';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const ArchiveOvr = () => {
  const dispatch = useDispatch();
  const [summary, setSummary] = useState(null);
  const [hidden, setHidden] = useState([]);
  const [totalHidden, setTotalHidden] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hiding, setHiding] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hideConfirm, setHideConfirm] = useState('');
  const [unhideConfirm, setUnhideConfirm] = useState('');
  const [deleteArchivedConfirm, setDeleteArchivedConfirm] = useState('');
  const [deleteAllConfirm, setDeleteAllConfirm] = useState('');
  const [pendingAction, setPendingAction] = useState(null);

  const loadSummary = useCallback(async () => {
    const { data } = await getDataSummary();
    setSummary(data);
  }, []);

  const loadHidden = useCallback(async (nextPage = page, nextSearch = search) => {
    const { data } = await getHiddenOvrRecords({
      page: nextPage,
      limit: 20,
      search: nextSearch,
    });
    setHidden(data.incidents || []);
    setTotalHidden(data.total || 0);
    setPage(data.page || nextPage);
    setSelected([]);
  }, [page, search]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadSummary(), loadHidden(1, search)]);
    } catch (error) {
      dispatch(setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Could not load archive data',
      }));
    } finally {
      setLoading(false);
    }
  }, [dispatch, loadHidden, loadSummary, search]);

  useEffect(() => {
    refresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const busy = hiding || restoring || deleting;

  const closeConfirm = () => {
    if (busy) return;
    setPendingAction(null);
  };

  const requestAction = (action) => {
    setPendingAction(action);
  };

  const runPendingAction = async () => {
    if (!pendingAction) return;
    const action = pendingAction;

    try {
      if (action.type === 'hide-all') {
        setHiding(true);
        const { data } = await hideAllOvrRecords('HIDE OVR');
        dispatch(setAlert({ type: 'success', message: data.message || 'OVR records archived.' }));
        setHideConfirm('');
      } else if (action.type === 'restore-selected') {
        setRestoring(true);
        const { data } = await unhideOvrRecords({ ids: selected });
        dispatch(setAlert({ type: 'success', message: data.message || 'Selected OVR records restored.' }));
      } else if (action.type === 'restore-all') {
        setRestoring(true);
        const { data } = await unhideOvrRecords({ all: true, confirm: 'UNHIDE OVR' });
        dispatch(setAlert({ type: 'success', message: data.message || 'Archived OVR records restored.' }));
        setUnhideConfirm('');
      } else if (action.type === 'delete-selected') {
        setDeleting(true);
        const { data } = await deleteOvrRecords({ scope: 'selected', ids: selected });
        dispatch(setAlert({ type: 'success', message: data.message || 'Selected OVR records deleted.' }));
      } else if (action.type === 'delete-archived') {
        setDeleting(true);
        const { data } = await deleteOvrRecords({
          scope: 'archived',
          confirm: 'DELETE ARCHIVED',
        });
        dispatch(setAlert({ type: 'success', message: data.message || 'Archived OVR records deleted.' }));
        setDeleteArchivedConfirm('');
      } else if (action.type === 'delete-all') {
        setDeleting(true);
        const { data } = await deleteOvrRecords({
          scope: 'all',
          confirm: 'DELETE OVR',
        });
        dispatch(setAlert({ type: 'success', message: data.message || 'All OVR records deleted.' }));
        setDeleteAllConfirm('');
      }

      setPendingAction(null);
      await refresh();
    } catch (error) {
      dispatch(setAlert({
        type: 'danger',
        message: error.response?.data?.error || action.errorMessage || 'Action failed',
      }));
    } finally {
      setHiding(false);
      setRestoring(false);
      setDeleting(false);
    }
  };

  const handleHideAll = (event) => {
    event.preventDefault();
    if (hideConfirm.trim() !== 'HIDE OVR') {
      dispatch(setAlert({ type: 'danger', message: 'Type HIDE OVR to confirm.' }));
      return;
    }
    requestAction({
      type: 'hide-all',
      title: 'Hide all current OVR?',
      message: `This will archive ${summary?.visibleIncidents || 0} visible OVR record(s).\nThey will disappear from dashboard, reports, and counts, but stay stored.\n\nContinue?`,
      confirmLabel: 'Yes, hide OVR',
      variant: 'warning',
      errorMessage: 'Could not archive OVR records',
    });
  };

  const toggleSelected = (id) => {
    setSelected((current) => (
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    ));
  };

  const toggleSelectAll = () => {
    if (selected.length === hidden.length) {
      setSelected([]);
      return;
    }
    setSelected(hidden.map((item) => item._id));
  };

  const handleRestoreSelected = () => {
    if (!selected.length) {
      dispatch(setAlert({ type: 'danger', message: 'Select at least one archived OVR.' }));
      return;
    }
    requestAction({
      type: 'restore-selected',
      title: 'Restore selected OVR?',
      message: `Restore ${selected.length} selected archived OVR record(s) so they are visible again?`,
      confirmLabel: 'Yes, restore',
      variant: 'primary',
      errorMessage: 'Could not restore selected OVR records',
    });
  };

  const handleRestoreAll = (event) => {
    event.preventDefault();
    if (unhideConfirm.trim() !== 'UNHIDE OVR') {
      dispatch(setAlert({ type: 'danger', message: 'Type UNHIDE OVR to confirm.' }));
      return;
    }
    requestAction({
      type: 'restore-all',
      title: 'Restore all archived OVR?',
      message: `Restore all ${totalHidden} archived OVR record(s) and make them visible again?`,
      confirmLabel: 'Yes, restore all',
      variant: 'primary',
      errorMessage: 'Could not restore archived OVR records',
    });
  };

  const handleDeleteSelected = () => {
    if (!selected.length) {
      dispatch(setAlert({ type: 'danger', message: 'Select at least one archived OVR to delete.' }));
      return;
    }
    requestAction({
      type: 'delete-selected',
      title: 'Permanently delete selected OVR?',
      message: `Permanently delete ${selected.length} selected archived OVR record(s)?\nThis cannot be undone.\nThe next OVR number will not go backwards.`,
      confirmLabel: 'Yes, delete',
      variant: 'danger',
      errorMessage: 'Could not delete selected OVR records',
    });
  };

  const handleDeleteArchived = (event) => {
    event.preventDefault();
    if (deleteArchivedConfirm.trim() !== 'DELETE ARCHIVED') {
      dispatch(setAlert({ type: 'danger', message: 'Type DELETE ARCHIVED to confirm.' }));
      return;
    }
    requestAction({
      type: 'delete-archived',
      title: 'Permanently delete archived OVR?',
      message: `Permanently delete all ${totalHidden} archived OVR record(s)?\nThis cannot be undone.\nThe next OVR number will not go backwards.`,
      confirmLabel: 'Yes, delete archived',
      variant: 'danger',
      errorMessage: 'Could not delete archived OVR records',
    });
  };

  const handleDeleteAll = (event) => {
    event.preventDefault();
    if (deleteAllConfirm.trim() !== 'DELETE OVR') {
      dispatch(setAlert({ type: 'danger', message: 'Type DELETE OVR to confirm.' }));
      return;
    }
    requestAction({
      type: 'delete-all',
      title: 'Permanently delete all OVR?',
      message: `Permanently delete all ${summary?.incidents || 0} OVR record(s) (visible and archived)?\nThis cannot be undone.\nThe next OVR number will not go backwards.`,
      confirmLabel: 'Yes, delete all OVR',
      variant: 'danger',
      errorMessage: 'Could not delete OVR records',
    });
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await loadHidden(1, search);
    } catch (error) {
      dispatch(setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Could not search archived OVR records',
      }));
    } finally {
      setLoading(false);
    }
  };

  if (loading && !summary) {
    return (
      <div className="admin-page d-flex justify-content-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(totalHidden / 20));

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h3>Hide / Archive OVR</h3>
          <p>
            Soft-hide current OVR records so they are not counted or shown on the dashboard,
            OVR registry reports, Excel/PDF exports, detail pages, or notification lists.
            Records stay stored and can be restored later. This does not delete data.
          </p>
        </div>
      </div>

      {summary && (
        <div className="data-summary card p-3 shadow-sm">
          <span><strong>{summary.visibleIncidents ?? summary.incidents}</strong> visible OVR</span>
          <span><strong>{summary.hiddenIncidents || 0}</strong> archived OVR</span>
          <span><strong>{summary.incidents}</strong> total stored</span>
          <span><strong>{summary.refNo?.nextRefNo || '—'}</strong> next OVR no.</span>
        </div>
      )}

      <Form onSubmit={handleHideAll} className="card p-3 shadow-sm data-tool-card">
        <h4>Hide all current OVR</h4>
        <p>
          Archives every visible OVR immediately. They will not appear in dashboard counts, lists,
          reports, or exports until restored. Use Backup & Reset only for a hard delete / factory reset.
        </p>
        <Form.Group controlId="hide-confirm" className="mb-3">
          <Form.Label>Type HIDE OVR to confirm</Form.Label>
          <Form.Control
            value={hideConfirm}
            onChange={(event) => setHideConfirm(event.target.value)}
            placeholder="HIDE OVR"
            autoComplete="off"
          />
        </Form.Group>
        <Button type="submit" variant="warning" disabled={busy || !(summary?.visibleIncidents > 0)}>
          {hiding ? 'Archiving...' : 'Hide all current OVR'}
        </Button>
      </Form>

      <section className="card p-3 shadow-sm data-tool-card">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div>
            <h4 className="mb-1">Archived OVR list</h4>
            <p className="mb-0">Restore selected records, or restore everything.</p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline-primary"
              disabled={busy || !selected.length}
              onClick={handleRestoreSelected}
            >
              {restoring ? 'Restoring...' : `Restore selected (${selected.length})`}
            </Button>
            <Button
              type="button"
              variant="outline-danger"
              disabled={busy || !selected.length}
              onClick={handleDeleteSelected}
            >
              {deleting ? 'Deleting...' : `Delete selected (${selected.length})`}
            </Button>
          </div>
        </div>

        <Form onSubmit={handleSearch} className="d-flex flex-wrap gap-2 mb-3">
          <Form.Control
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search ref no, name, or status"
            style={{ maxWidth: 320 }}
          />
          <Button type="submit" variant="secondary">Search</Button>
        </Form>

        <div className="table-responsive">
          <Table hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th>
                  <Form.Check
                    type="checkbox"
                    checked={hidden.length > 0 && selected.length === hidden.length}
                    onChange={toggleSelectAll}
                    aria-label="Select all archived OVR on this page"
                  />
                </th>
                <th>Ref No</th>
                <th>Name</th>
                <th>Status</th>
                <th>Department</th>
                <th>Hidden at</th>
                <th>Hidden by</th>
              </tr>
            </thead>
            <tbody>
              {!hidden.length && (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    No archived OVR records.
                  </td>
                </tr>
              )}
              {hidden.map((item) => (
                <tr key={item._id}>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={selected.includes(item._id)}
                      onChange={() => toggleSelected(item._id)}
                      aria-label={`Select ${item.refNo}`}
                    />
                  </td>
                  <td>{item.refNo}</td>
                  <td>{item.name}</td>
                  <td>{item.currentStatus}</td>
                  <td>{item.department?.departmentName || '—'}</td>
                  <td>{formatDate(item.hiddenAt)}</td>
                  <td>{item.hiddenBy?.username || '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {totalHidden > 20 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="text-muted">Page {page} of {totalPages}</span>
            <div className="d-flex gap-2">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page <= 1 || loading}
                onClick={async () => {
                  setLoading(true);
                  try {
                    await loadHidden(page - 1, search);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page >= totalPages || loading}
                onClick={async () => {
                  setLoading(true);
                  try {
                    await loadHidden(page + 1, search);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </section>

      <Form onSubmit={handleRestoreAll} className="card p-3 shadow-sm data-tool-card">
        <h4>Restore all archived OVR</h4>
        <p>Makes every archived OVR visible again across the system.</p>
        <Form.Group controlId="unhide-confirm" className="mb-3">
          <Form.Label>Type UNHIDE OVR to confirm</Form.Label>
          <Form.Control
            value={unhideConfirm}
            onChange={(event) => setUnhideConfirm(event.target.value)}
            placeholder="UNHIDE OVR"
            autoComplete="off"
          />
        </Form.Group>
        <Button type="submit" disabled={busy || totalHidden < 1}>
          {restoring ? 'Restoring...' : 'Restore all archived OVR'}
        </Button>
      </Form>

      <Form onSubmit={handleDeleteArchived} className="card p-3 shadow-sm data-tool-card data-tool-danger">
        <h4>Permanently delete archived OVR</h4>
        <p>
          Removes archived OVR records and related workflow data. Useful for clearing test data that
          was already hidden. The next auto OVR number stays the same sequence and does not go backwards.
        </p>
        <Form.Group controlId="delete-archived-confirm" className="mb-3">
          <Form.Label>Type DELETE ARCHIVED to confirm</Form.Label>
          <Form.Control
            value={deleteArchivedConfirm}
            onChange={(event) => setDeleteArchivedConfirm(event.target.value)}
            placeholder="DELETE ARCHIVED"
            autoComplete="off"
          />
        </Form.Group>
        <Button type="submit" variant="danger" disabled={busy || totalHidden < 1}>
          {deleting ? 'Deleting...' : 'Delete all archived OVR'}
        </Button>
      </Form>

      <Form onSubmit={handleDeleteAll} className="card p-3 shadow-sm data-tool-card data-tool-danger">
        <h4>Permanently delete all OVR</h4>
        <p>
          Deletes every OVR (visible and archived) and related records. Roles, users, departments, and
          settings stay. The next OVR unique number continues from the highest number already used.
        </p>
        <Form.Group controlId="delete-all-confirm" className="mb-3">
          <Form.Label>Type DELETE OVR to confirm</Form.Label>
          <Form.Control
            value={deleteAllConfirm}
            onChange={(event) => setDeleteAllConfirm(event.target.value)}
            placeholder="DELETE OVR"
            autoComplete="off"
          />
        </Form.Group>
        <Button type="submit" variant="danger" disabled={busy || !(summary?.incidents > 0)}>
          {deleting ? 'Deleting...' : 'Delete all OVR records'}
        </Button>
      </Form>

      <ConfirmActionModal
        show={Boolean(pendingAction)}
        title={pendingAction?.title}
        message={pendingAction?.message}
        confirmLabel={pendingAction?.confirmLabel}
        variant={pendingAction?.variant || 'primary'}
        busy={busy}
        onCancel={closeConfirm}
        onConfirm={runPendingAction}
      />
    </div>
  );
};

export default ArchiveOvr;
